/**
 * Premium Kundli Report Page
 * NASA-grade Vedic Kundli with all 9 grahas, D9, D10, dashas (3 levels),
 * transit (gochar), Sade Sati, AI Phaladesh, PDF download.
 *
 * Design: Dark cosmic theme + gold accents + diamond/SVG charts
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Download, Sparkles, Star, Sun, Moon, Loader2,
  TrendingUp, Heart, Briefcase, Coins, HeartPulse, GraduationCap,
  Calendar, Clock, MapPin, AlertTriangle, CheckCircle2, Mail, Phone, X
} from 'lucide-react';
import VedicChart from '../components/VedicChart';
import './KundliReport.css';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'kundli', label: 'Kundli', labelHi: 'कुंडली' },
  { id: 'planets', label: 'Grah Sthiti', labelHi: 'ग्रह स्थिति' },
  { id: 'strength', label: 'Bal Chakra', labelHi: 'ग्रह बल' },
  { id: 'dashas', label: 'Dasha', labelHi: 'दशा' },
  { id: 'predictions', label: 'Phaladesh', labelHi: 'फलादेश' },
];

export default function KundliReport() {
  const navigate = useNavigate();
  const location = useLocation();
  const reportRef = useRef(null);

  const passed = location.state?.kundli;
  const formData = location.state?.formData;
  const [kundli] = useState(passed);
  const [activeTab, setActiveTab] = useState('kundli');
  const [transit, setTransit] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [strength, setStrength] = useState(null);
  const [loadingStrength, setLoadingStrength] = useState(false);
  const [loadingTransit, setLoadingTransit] = useState(false);
  const [loadingPred, setLoadingPred] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadForm, setLeadForm] = useState({ email: '', phone: '' });
  const [leadStatus, setLeadStatus] = useState(''); // '', 'sending', 'success', 'error'
  const [pdfShareUrl, setPdfShareUrl] = useState('');
  const [isSelfSend, setIsSelfSend] = useState(false);

  useEffect(() => {
    if (!kundli) {
      navigate('/#kundli');
    }
  }, [kundli, navigate]);

  const fetchTransit = async () => {
    if (transit || loadingTransit) return;
    setLoadingTransit(true);
    try {
      const res = await axios.post(`${API}/api/kundli/transit`, formData);
      setTransit(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingTransit(false);
    }
  };

  const fetchPredictions = async () => {
    if (predictions || loadingPred) return;
    setLoadingPred(true);
    try {
      const res = await axios.post(`${API}/api/kundli/predictions`, { ...formData, language: 'hi' });
      setPredictions(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingPred(false);
    }
  };

  const fetchStrength = async () => {
    if (strength || loadingStrength) return;
    setLoadingStrength(true);
    try {
      const res = await axios.post(`${API}/api/kundli/strength`, formData);
      setStrength(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingStrength(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'predictions') {
      fetchTransit();
      fetchPredictions();
      fetchStrength();
    }
    if (activeTab === 'strength') {
      fetchStrength();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleDownloadPDF = async () => {
    // Open lead capture modal first
    setShowLeadModal(true);
    setLeadStatus('');
  };

  const generateAndProcessPDF = async () => {
    if (!leadForm.email || !leadForm.phone) {
      setLeadStatus('error');
      return;
    }
    setLeadStatus('sending');
    setDownloading(true);

    try {
      // Pre-load all sections so they render in PDF
      if (!transit) await fetchTransit();
      if (!predictions) await fetchPredictions();
      // Wait a bit for lazy sections to render
      await new Promise(r => setTimeout(r, 600));

      const html2pdf = (await import('html2pdf.js')).default;
      const el = reportRef.current;
      if (!el) throw new Error('Report element not found');
      el.classList.add('pdf-mode');

      const filename = `Kundli-${kundli?.name || 'Report'}-${new Date().toISOString().split('T')[0]}.pdf`;

      // Generate as blob to send to backend, then download locally
      const worker = html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          filename,
          image: { type: 'jpeg', quality: 0.92 },
          html2canvas: { scale: 1.6, backgroundColor: '#0a0719', useCORS: true, letterRendering: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['css', 'legacy'] }
        })
        .from(el);

      const pdfBlob = await worker.outputPdf('blob');
      el.classList.remove('pdf-mode');

      // Convert to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(pdfBlob);
      });

      // Send to backend (lead + email)
      const summary = {
        ascendant: kundli?.ascendant?.name,
        moonSign: kundli?.moonSign?.name,
        nakshatra: kundli?.nakshatra,
      };

      await axios.post(`${API}/api/kundli/email-pdf`, {
        name: kundli?.name || 'Guest',
        email: leadForm.email,
        phone: leadForm.phone,
        birth_date: formData?.birth_date || '',
        birth_time: formData?.birth_time || '',
        birth_place: formData?.birth_place || '',
        pdf_base64: base64,
        summary,
      }).then(res => {
        setPdfShareUrl(res.data?.pdf_url || '');
        setIsSelfSend(!!res.data?.is_self_send);
      });

      // Trigger local download
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);

      setLeadStatus('success');
    } catch (e) {
      console.error('PDF flow error:', e);
      setLeadStatus('error');
    } finally {
      setDownloading(false);
    }
  };

  if (!kundli) return null;

  const activeMd = kundli.dashas?.find(d => d.active);
  const activeAd = kundli.antardashas?.find(a => a.active);
  const activePd = kundli.pratyantars?.find(p => p.active);

  return (
    <div className="kr-page" data-testid="kundli-report-page">
      {/* Lead Capture Modal */}
      {showLeadModal && (
        <div className="kr-modal-backdrop no-print" onClick={() => leadStatus !== 'sending' && setShowLeadModal(false)}>
          <div className="kr-modal" onClick={(e) => e.stopPropagation()} data-testid="kr-lead-modal">
            <button className="kr-modal-close" onClick={() => setShowLeadModal(false)} disabled={leadStatus === 'sending'}>
              <X className="w-4 h-4" />
            </button>

            {leadStatus === 'success' ? (
              <div className="kr-modal-success">
                <CheckCircle2 className="w-14 h-14" style={{ color: '#4ade80' }} />
                <h3>Hey {kundli?.name} ji! 🎉</h3>
                <p className="kr-modal-success-msg">PDF आपकी email पर भेज दी गई है और download भी हो गई है।</p>
                {!isSelfSend && <p className="kr-modal-success-sub">आपके WhatsApp पर भी PDF link आ जाएगा।</p>}
                {isSelfSend && <p className="kr-modal-success-sub" style={{ color: '#fbbf24' }}>⚠️ आपने अपना ही business number दिया है — WhatsApp पर message नहीं जा सकता (Twilio की policy)। PDF नीचे link से share करें।</p>}

                {pdfShareUrl && (
                  <div className="kr-pdf-share-box">
                    <label>PDF Share Link:</label>
                    <div className="kr-pdf-url-row">
                      <input type="text" readOnly value={pdfShareUrl} data-testid="pdf-share-url" onClick={(e) => e.target.select()} />
                      <button
                        onClick={() => { navigator.clipboard.writeText(pdfShareUrl); alert('Link copied!'); }}
                        data-testid="copy-pdf-link"
                      >Copy</button>
                    </div>
                    <div className="kr-pdf-share-actions">
                      <a
                        href={`https://wa.me/?text=${encodeURIComponent(`मेरी वैदिक कुंडली रिपोर्ट: ${pdfShareUrl}`)}`}
                        target="_blank" rel="noopener noreferrer"
                        className="kr-wa-share-btn"
                        data-testid="share-via-whatsapp"
                      >📱 WhatsApp पर Share करें</a>
                      <a
                        href={pdfShareUrl}
                        target="_blank" rel="noopener noreferrer"
                        className="kr-view-pdf-btn"
                        data-testid="view-pdf"
                      >📄 PDF देखें</a>
                    </div>
                  </div>
                )}

                <button className="kr-modal-btn-primary" onClick={() => setShowLeadModal(false)}>
                  ठीक है / OK
                </button>
              </div>
            ) : (
              <>
                <div className="kr-modal-head">
                  <Sparkles className="w-6 h-6" style={{ color: '#FFD700' }} />
                  <h3>Free Kundli PDF प्राप्त करें</h3>
                  <p>हम आपकी विस्तृत कुंडली रिपोर्ट email पर भेजेंगे + तुरंत download करवाएंगे</p>
                </div>

                <div className="kr-modal-body">
                  <label className="kr-field">
                    <span><Mail className="w-3 h-3 inline mr-1" /> ईमेल / Email *</span>
                    <input
                      type="email"
                      value={leadForm.email}
                      onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                      placeholder="aap@example.com"
                      data-testid="kr-lead-email"
                      disabled={leadStatus === 'sending'}
                    />
                  </label>
                  <label className="kr-field">
                    <span><Phone className="w-3 h-3 inline mr-1" /> WhatsApp Number *</span>
                    <input
                      type="tel"
                      value={leadForm.phone}
                      onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                      placeholder="+91 98XXXXXXXX"
                      data-testid="kr-lead-phone"
                      disabled={leadStatus === 'sending'}
                    />
                  </label>

                  {leadStatus === 'error' && (
                    <div className="kr-modal-error">
                      <AlertTriangle className="w-4 h-4" /> कुछ गड़बड़ हुई — कृपया दोबारा कोशिश करें या email/phone check करें
                    </div>
                  )}

                  <button
                    className="kr-modal-btn-primary"
                    onClick={generateAndProcessPDF}
                    disabled={leadStatus === 'sending' || !leadForm.email || !leadForm.phone}
                    data-testid="kr-lead-submit"
                  >
                    {leadStatus === 'sending'
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> PDF तैयार हो रही है...</>
                      : <><Download className="w-4 h-4" /> PDF Email + Download</>}
                  </button>
                  <p className="kr-modal-disclaim">हम आपकी privacy का सम्मान करते हैं। केवल Vivek Mudgal Astrologer आपसे संपर्क करेंगे।</p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Top bar */}
      <div className="kr-topbar no-print">
        <button onClick={() => navigate(-1)} className="kr-back" data-testid="kr-back-btn">
          <ArrowLeft className="w-4 h-4" /> वापस / Back
        </button>
        <button
          onClick={handleDownloadPDF}
          disabled={downloading}
          className="kr-download"
          data-testid="kr-download-pdf-btn"
        >
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          {downloading ? 'PDF बन रहा है...' : 'Download PDF'}
        </button>
      </div>

      {/* Tab nav */}
      <div className="kr-tabs no-print">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`kr-tab ${activeTab === tab.id ? 'active' : ''}`}
            data-testid={`kr-tab-${tab.id}`}
          >
            <span className="kr-tab-en">{tab.label}</span>
            <span className="kr-tab-hi">{tab.labelHi}</span>
          </button>
        ))}
      </div>

      <div ref={reportRef} className="kr-content">
        {/* Header (always visible in PDF) */}
        <div className="kr-header">
          <div className="kr-brand">
            <Sparkles className="w-6 h-6 text-[#FFD700]" />
            <div>
              <div className="kr-brand-name">ViratAstro</div>
              <div className="kr-brand-tag">Vivek Mudgal Astrologer · 33+ Years Experience</div>
            </div>
          </div>
          <div className="kr-doctype">वैदिक जन्म कुंडली · Vedic Birth Chart</div>
          <div className="kr-engine-badge">
            <CheckCircle2 className="w-3 h-3" /> महर्षि पराशर के प्राचीन सिद्धांत · Lahiri Ayanamsa · High-Precision Calculations
          </div>
        </div>

        {/* Native info card */}
        <div className="kr-native">
          <div className="kr-native-row">
            <div><span className="kr-label">नाम / Name</span><span className="kr-value">{kundli.name || '—'}</span></div>
            <div><Calendar className="w-3 h-3 inline mr-1" /><span className="kr-value">{kundli.birthDate}</span></div>
            <div><Clock className="w-3 h-3 inline mr-1" /><span className="kr-value">{kundli.birthTime}</span></div>
            <div><MapPin className="w-3 h-3 inline mr-1" /><span className="kr-value">{kundli.birthPlace}</span></div>
          </div>
        </div>

        {/* KUNDLI (Overview + Charts merged) */}
        {(activeTab === 'kundli' || downloading) && (
          <section className="kr-section">
            <h2 className="kr-h2"><Star className="w-5 h-5" /> मुख्य जानकारी / Key Highlights</h2>
            <div className="kr-grid-4">
              <HighlightCard icon={<Sun />} label="लग्न / Ascendant"
                main={kundli.ascendant?.nameHi} sub={`${kundli.ascendant?.name} · ${kundli.ascendantDegree}°`} />
              <HighlightCard icon={<Sun />} label="सूर्य राशि / Sun Sign (Vedic)"
                main={kundli.sunSign?.nameHi} sub={kundli.sunSign?.name} />
              <HighlightCard icon={<Moon />} label="चंद्र राशि / जन्म राशि"
                main={kundli.moonSign?.nameHi} sub={kundli.moonSign?.name} />
              <HighlightCard icon={<Star />} label="नक्षत्र / Nakshatra"
                main={kundli.nakshatra?.split(' / ')[1] || kundli.nakshatra}
                sub={`पाद ${kundli.nakshatraPada} · Pada ${kundli.nakshatraPada}`} />
            </div>

            <div className="kr-sun-note">
              💡 <strong>नोट:</strong> भारतीय वैदिक परंपरा में "राशि" से अभिप्राय <strong>चंद्र राशि (जन्म राशि)</strong> होती है। Western horoscope में जो "Sun sign" होती है वो Tropical calculation से होती है, जबकि यहाँ दिखाई गई सूर्य राशि <strong>वैदिक Sidereal (Lahiri)</strong> गणना पर आधारित है।
            </div>

            {/* Manglik */}
            <div className={`kr-manglik kr-manglik-${kundli.manglik?.severity}`}>
              <div className="kr-manglik-head">
                {kundli.manglik?.severity === 'none' ? <CheckCircle2 className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
                <strong>मांगलिक विश्लेषण / Manglik Analysis</strong>
                <span className="kr-pill">{kundli.manglik?.severity?.toUpperCase()}</span>
              </div>
              <p>{kundli.manglik?.explanation_hi}</p>
              <div className="kr-manglik-meta">
                <span>लग्न से मंगल: <strong>House {kundli.manglik?.mars_house_from_lagna}</strong></span>
                <span>चंद्र से मंगल: <strong>House {kundli.manglik?.mars_house_from_moon}</strong></span>
              </div>
            </div>

            {/* Lucky */}
            <div className="kr-lucky">
              <div><span>शुभ रंग</span><strong>{kundli.luckyColor}</strong></div>
              <div><span>शुभ अंक</span><strong>{kundli.luckyNumbers}</strong></div>
              <div><span>शुभ दिन</span><strong>{kundli.luckyDay}</strong></div>
              <div><span>अयनांश (Lahiri)</span><strong>{kundli.ayanamsa}°</strong></div>
            </div>

            {/* Current periods */}
            {activeMd && (
              <div className="kr-active-dasha">
                <h3 className="kr-h3">वर्तमान दशा / Current Periods</h3>
                <div className="kr-dasha-row">
                  <DashaPill level="महादशा / MD" lord={activeMd.lord} period={`${activeMd.start} → ${activeMd.end}`} />
                  {activeAd && <DashaPill level="अंतर्दशा / AD" lord={activeAd.lord} period={`${activeAd.start} → ${activeAd.end}`} />}
                  {activePd && <DashaPill level="प्रत्यंतर / PD" lord={activePd.lord} period={`${activePd.start} → ${activePd.end}`} />}
                </div>
              </div>
            )}

            {/* Divisional charts */}
            <h3 className="kr-h3 kr-mt"><Star className="w-4 h-4 inline mr-1" /> कुंडली चार्ट / Divisional Charts</h3>
            <div className="kr-rashi-legend">
              <strong>राशि संख्या:</strong>
              <span>1=मेष · 2=वृष · 3=मिथुन · 4=कर्क · 5=सिंह · 6=कन्या · 7=तुला · 8=वृश्चिक · 9=धनु · 10=मकर · 11=कुंभ · 12=मीन</span>
            </div>
            <div className="kr-charts-grid">
              <div className="kr-chart-block">
                <h3 className="kr-h3">D1 · राशि चार्ट / Birth Chart</h3>
                <VedicChart
                  lagnaSignIdx={getSignIdx(kundli.ascendant?.name)}
                  planets={kundli.planets || []}
                  testId="d1-chart"
                />
              </div>
              <div className="kr-chart-block">
                <h3 className="kr-h3">D9 · नवांश / Marriage Chart</h3>
                <VedicChart
                  lagnaSignIdx={getSignIdx(kundli.navamsa?.lagna?.name)}
                  planets={kundli.navamsa?.planets?.map(p => ({
                    name: p.name, nameHi: p.nameHi,
                    signIdx: getSignIdx(p.sign), house: p.house
                  })) || []}
                  testId="d9-chart"
                />
              </div>
              <div className="kr-chart-block">
                <h3 className="kr-h3">D10 · दशमांश / Career Chart</h3>
                <VedicChart
                  lagnaSignIdx={getSignIdx(kundli.dashamsa?.lagna?.name)}
                  planets={kundli.dashamsa?.planets?.map(p => ({
                    name: p.name, nameHi: p.nameHi,
                    signIdx: getSignIdx(p.sign), house: p.house
                  })) || []}
                  testId="d10-chart"
                />
              </div>
            </div>
          </section>
        )}

        {/* PLANETS with Nakshatra Lord */}
        {(activeTab === 'planets' || downloading) && (
          <section className="kr-section">
            <h2 className="kr-h2"><Sparkles className="w-5 h-5" /> ग्रह स्थिति / All 9 Grahas</h2>
            <div className="kr-table-wrap">
              <table className="kr-table">
                <thead>
                  <tr>
                    <th>ग्रह / Planet</th>
                    <th>राशि / Sign</th>
                    <th>अंश / Degree</th>
                    <th>भाव / House</th>
                    <th>नक्षत्र / Nakshatra</th>
                    <th>चरण / Pada</th>
                    <th>नक्षत्र स्वामी / Nak Lord</th>
                    <th>स्थिति / State</th>
                  </tr>
                </thead>
                <tbody>
                  {kundli.planets?.map((p, i) => (
                    <tr key={i} data-testid={`planet-row-${p.name}`}>
                      <td><strong>{p.nameHi}</strong> <span className="kr-muted">/ {p.name}</span></td>
                      <td>{p.signHi} <span className="kr-muted">/ {p.sign}</span></td>
                      <td className="kr-mono">{p.degree.toFixed(2)}°</td>
                      <td className="kr-mono"><span className="kr-house-badge">{p.house}</span></td>
                      <td>{p.nakshatra?.split(' / ')[1] || p.nakshatra}</td>
                      <td className="kr-mono">{p.pada}</td>
                      <td className="kr-muted kr-small">{p.nakshatraLord}</td>
                      <td>
                        {p.retrograde && <span className="kr-tag-r">वक्री / R</span>}
                        {!p.retrograde && <span className="kr-tag-d">मार्गी / D</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* DASHAS */}
        {(activeTab === 'dashas' || downloading) && (
          <section className="kr-section">
            <h2 className="kr-h2"><TrendingUp className="w-5 h-5" /> विंशोत्तरी दशा / Vimshottari Dasha System</h2>

            <h3 className="kr-h3">महादशा / Mahadasha (120 वर्ष चक्र)</h3>
            <div className="kr-dasha-list">
              {kundli.dashas?.map((d, i) => (
                <div key={i} className={`kr-dasha-item ${d.active ? 'active' : ''}`} data-testid={`md-${d.lord}`}>
                  <div className="kr-dasha-lord">{d.lordHi || d.lord}</div>
                  <div className="kr-dasha-period">{d.start} → {d.end}</div>
                  <div className="kr-dasha-years">{d.years} वर्ष</div>
                  {d.active && <span className="kr-active-tag">वर्तमान / Current</span>}
                </div>
              ))}
            </div>

            <h3 className="kr-h3 kr-mt">अंतर्दशा / Antardasha (वर्तमान महादशा में)</h3>
            <div className="kr-dasha-list">
              {kundli.antardashas?.map((a, i) => (
                <div key={i} className={`kr-dasha-item ${a.active ? 'active' : ''}`} data-testid={`ad-${a.lord}`}>
                  <div className="kr-dasha-lord">{a.lordHi || a.lord}</div>
                  <div className="kr-dasha-period">{a.start} → {a.end}</div>
                  <div className="kr-dasha-years">{a.years} वर्ष</div>
                  {a.active && <span className="kr-active-tag">वर्तमान</span>}
                </div>
              ))}
            </div>

            <h3 className="kr-h3 kr-mt">प्रत्यंतर दशा / Pratyantar Dasha (वर्तमान अंतर्दशा में)</h3>
            <div className="kr-dasha-list">
              {kundli.pratyantars?.map((p, i) => (
                <div key={i} className={`kr-dasha-item ${p.active ? 'active' : ''}`} data-testid={`pd-${p.lord}`}>
                  <div className="kr-dasha-lord">{p.lordHi || p.lord}</div>
                  <div className="kr-dasha-period">{p.start} → {p.end}</div>
                  <div className="kr-dasha-years">{p.days} दिन</div>
                  {p.active && <span className="kr-active-tag">वर्तमान</span>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* TRANSIT - merged into Predictions tab */}
        {false && (activeTab === 'transit' || downloading) && (
          <section className="kr-section"></section>
        )}

        {/* STRENGTH (Bal Chakra) — Deterministic Parashari */}
        {(activeTab === 'strength' || downloading) && (
          <section className="kr-section">
            <h2 className="kr-h2"><Star className="w-5 h-5" /> ग्रह बल चक्र / Parashari Strength Engine</h2>
            <p className="kr-sub">पराशर सिद्धांत से Swiss Ephemeris आधारित गणितीय बल — कोई अनुमान नहीं।</p>

            {loadingStrength && !strength && (
              <div className="kr-loader"><div className="kr-spinner" /><p>आचार्य के कैलकुलेशन Engine से बल निकाला जा रहा है...</p></div>
            )}

            {strength && (
              <div className="kr-strength-wrap">
                {/* GRAHA BAL TABLE */}
                <h3 className="kr-h3">ग्रह बल / Graha Bala (Score 0-100)</h3>
                <div className="kr-strength-grid">
                  {Object.values(strength.graha_bal || {}).map((gb) => {
                    const pct = gb.score;
                    const color = pct >= 70 ? '#10b981' : pct >= 50 ? '#f59e0b' : pct >= 30 ? '#f97316' : '#ef4444';
                    const tags = [];
                    if (gb.exalted) tags.push('उच्च');
                    if (gb.debilitated) tags.push('नीच');
                    if (gb.own_sign) tags.push('स्व-राशि');
                    if (gb.moolatrikona) tags.push('मूलत्रिकोण');
                    if (gb.combust) tags.push('अस्त');
                    if (gb.retrograde) tags.push('वक्री');
                    return (
                      <div key={gb.name} className="kr-grah-card" data-testid={`strength-${gb.name.toLowerCase()}`}>
                        <div className="kr-grah-head">
                          <span className="kr-grah-name">{gb.nameHi || gb.name}</span>
                          <span className="kr-grah-score" style={{ color }}>{gb.score}/100</span>
                        </div>
                        <div className="kr-grah-bar"><div className="kr-grah-bar-fill" style={{ width: `${pct}%`, background: color }} /></div>
                        <div className="kr-grah-meta">{gb.sign} • भाव {gb.house} • <strong>{gb.verdict_hi}</strong></div>
                        {tags.length > 0 && <div className="kr-grah-tags">{tags.map((t, i) => <span key={i} className="kr-grah-tag">{t}</span>)}</div>}
                      </div>
                    );
                  })}
                </div>

                {/* BHAVA BAL TABLE */}
                <h3 className="kr-h3 kr-mt">भाव बल / Bhava Bala — 12 भावों की मजबूती</h3>
                <div className="kr-strength-grid">
                  {(strength.bhava_bal || []).map((bb) => {
                    const pct = bb.score;
                    const color = pct >= 70 ? '#10b981' : pct >= 50 ? '#f59e0b' : pct >= 30 ? '#f97316' : '#ef4444';
                    return (
                      <div key={bb.house} className="kr-grah-card" data-testid={`bhava-${bb.house}`}>
                        <div className="kr-grah-head">
                          <span className="kr-grah-name">भाव {bb.house}</span>
                          <span className="kr-grah-score" style={{ color }}>{bb.score}/100</span>
                        </div>
                        <div className="kr-grah-bar"><div className="kr-grah-bar-fill" style={{ width: `${pct}%`, background: color }} /></div>
                        <div className="kr-grah-meta">{bb.sign} • स्वामी {bb.lord_hi} • <strong>{bb.verdict_hi}</strong></div>
                        {bb.occupants_hi?.length > 0 && (
                          <div className="kr-grah-tags">{bb.occupants_hi.map((n, i) => <span key={i} className="kr-grah-tag">{n}</span>)}</div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* YOGAS */}
                <h3 className="kr-h3 kr-mt">पराशरीय योग / Detected Yogas</h3>
                {strength.yogas?.length > 0 ? (
                  <div className="kr-yoga-list">
                    {strength.yogas.map((y, i) => (
                      <div key={i} className={`kr-yoga-card ${y.is_positive ? 'positive' : 'negative'}`} data-testid={`yoga-${i}`}>
                        <div className="kr-yoga-head">
                          <span className="kr-yoga-name">{y.name_hi}</span>
                          <span className="kr-yoga-badge">{y.is_positive ? 'शुभ' : 'सावधानी'}</span>
                        </div>
                        <p className="kr-yoga-desc">{y.description_hi}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="kr-sub">इस कुंडली में कोई प्रमुख classical योग auto-detect नहीं हुआ — पर इसका मतलब "कमजोर कुंडली" नहीं। व्यक्तिगत परामर्श में सूक्ष्म योग चर्चा होगी।</p>
                )}
              </div>
            )}
          </section>
        )}

        {/* PREDICTIONS (Phaladesh) - includes Gochar + Nakshatra + AI Predictions */}
        {(activeTab === 'predictions' || downloading) && (
          <section className="kr-section">
            <h2 className="kr-h2"><Sparkles className="w-5 h-5" /> फलादेश / Predictions by Vivek Mudgal</h2>
            {(loadingPred || loadingTransit) && !predictions && (
              <div className="kr-loader">
                <Loader2 className="animate-spin" /> गुरुजी आपकी कुंडली का विश्लेषण कर रहे हैं... (20-30 sec)
              </div>
            )}

            {/* GOCHAR / TRANSIT sub-section */}
            {transit && (
              <div className="kr-transit-box">
                <h3 className="kr-h3"><TrendingUp className="w-4 h-4 inline mr-2" />गोचर / Current Transit — {transit.date}</h3>
                {transit.sade_sati?.active && (
                  <div className="kr-sade-sati">
                    <AlertTriangle className="w-5 h-5" />
                    <div>
                      <strong>शनि साढ़ेसाती सक्रिय / Sade Sati Active</strong>
                      <p>वर्तमान चरण: <strong>{transit.sade_sati.phase_hi}</strong> ({transit.sade_sati.phase})</p>
                    </div>
                  </div>
                )}

                {transit.gochar_phaladesh && (
                  <div className="kr-gochar-phal">
                    <strong>गोचर फल:</strong>
                    <p>{transit.gochar_phaladesh}</p>
                  </div>
                )}

                <div className="kr-table-wrap">
                  <table className="kr-table">
                    <thead>
                      <tr>
                        <th>ग्रह / Planet</th>
                        <th>वर्तमान राशि / Sign</th>
                        <th>अंश</th>
                        <th>लग्न से भाव</th>
                        <th>प्रभाव</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transit.transit_planets?.map((p, i) => (
                        <tr key={i}>
                          <td><strong>{p.nameHi}</strong> / {p.name}</td>
                          <td>{p.currentSignHi}</td>
                          <td className="kr-mono">{p.degree.toFixed(2)}° {p.retrograde && <span className="kr-tag-r">R</span>}</td>
                          <td className="kr-mono"><span className="kr-house-badge">{p.houseFromNatalLagna}</span></td>
                          <td><span className={`kr-effect-${p.effect}`}>{p.effect}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {predictions && (
              <div className="kr-pred-grid">
                <PredCard icon={<Star />} title="व्यक्तित्व / Personality" body={predictions.personality} shloka={predictions.personality_shloka} />
                <PredCard icon={<Briefcase />} title="करियर / Career" body={predictions.career} shloka={predictions.career_shloka} />
                <PredCard icon={<Heart />} title="विवाह / Marriage" body={predictions.marriage} shloka={predictions.marriage_shloka} />
                <PredCard icon={<Coins />} title="धन योग / Wealth" body={predictions.wealth} shloka={predictions.wealth_shloka} />
                <PredCard icon={<HeartPulse />} title="स्वास्थ्य / Health" body={predictions.health} shloka={predictions.health_shloka} />
                <PredCard icon={<GraduationCap />} title="शिक्षा / Education" body={predictions.education} shloka={predictions.education_shloka} />
                {predictions.nakshatra_phala && (
                  <PredCard icon={<Star />} title="नक्षत्र फल / Nakshatra Phala" body={predictions.nakshatra_phala} shloka={predictions.nakshatra_shloka} className="kr-pred-wide" />
                )}
                <PredCard icon={<TrendingUp />} title="वर्तमान दशा फल / Current Period" body={predictions.current_period} shloka={predictions.current_period_shloka} className="kr-pred-wide" />

                {predictions.yogas?.length > 0 && (
                  <div className="kr-pred-card kr-pred-wide">
                    <div className="kr-pred-head"><Sparkles className="w-4 h-4" /> <strong>योग / Important Yogas</strong></div>
                    <ul className="kr-yoga-list">
                      {predictions.yogas.map((y, i) => <li key={i}>{y}</li>)}
                    </ul>
                  </div>
                )}

                {predictions.remedies?.length > 0 && (
                  <div className="kr-pred-card kr-pred-wide kr-remedies">
                    <div className="kr-pred-head"><CheckCircle2 className="w-4 h-4" /> <strong>उपाय / Recommended Remedies</strong></div>
                    <ol className="kr-remedy-list">
                      {predictions.remedies.map((r, i) => <li key={i}>{r}</li>)}
                    </ol>
                  </div>
                )}

                {/* Book Appointment CTA */}
                <div className="kr-book-cta kr-pred-wide no-print">
                  <h3>विस्तृत परामर्श चाहिए?</h3>
                  <p>Guruji Vivek Mudgal से personally बात करने के लिए अपॉइंटमेंट बुक करें</p>
                  <a
                    href={`https://wa.me/919311369729?text=${encodeURIComponent(`नमस्कार Guruji 🙏 मैं ${kundli?.name || ''} — मैंने अपनी जन्म कुंडली देखी है, परामर्श के लिए अपॉइंटमेंट बुक करना चाहता/चाहती हूं।`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="kr-book-btn"
                    data-testid="kr-book-appointment"
                  >
                    📱 परामर्श के लिए अपॉइंटमेंट बुक करें
                  </a>
                </div>
              </div>
            )}
          </section>
        )}

        {/* Footer (always in PDF) */}
        <div className="kr-footer">
          <div>© ViratAstro · Vivek Mudgal Astrologer</div>
          <div>📞 +91-9311369729 · 🌐 viratastro.cloud</div>
          <div className="kr-disclaim">आपकी विस्तृत जन्म कुंडली महर्षि पराशर के प्राचीन सिद्धांतों और उच्च सटीक गणितीय गणनाओं (High-Precision Calculations) द्वारा तैयार की गई है। व्यक्तिगत परामर्श के लिए कृपया गुरुजी से संपर्क करें।</div>
        </div>
      </div>
    </div>
  );
}

// Sub-components ----------------------------------------------------------
function HighlightCard({ icon, label, main, sub }) {
  return (
    <div className="kr-hl-card">
      <div className="kr-hl-icon">{icon}</div>
      <div className="kr-hl-label">{label}</div>
      <div className="kr-hl-main">{main}</div>
      <div className="kr-hl-sub">{sub}</div>
    </div>
  );
}

function DashaPill({ level, lord, period }) {
  return (
    <div className="kr-dasha-pill">
      <div className="kr-dasha-pill-level">{level}</div>
      <div className="kr-dasha-pill-lord">{lord}</div>
      <div className="kr-dasha-pill-period">{period}</div>
    </div>
  );
}

function PredCard({ icon, title, body, shloka, className = '' }) {
  return (
    <div className={`kr-pred-card ${className}`}>
      <div className="kr-pred-head">{icon}<strong>{title}</strong></div>
      <p>{body}</p>
      {shloka && shloka.sanskrit && (
        <div className="kr-shloka">
          <div className="kr-shloka-sanskrit">{shloka.sanskrit}</div>
          <div className="kr-shloka-meaning"><strong>अर्थ:</strong> {shloka.meaning}</div>
          <div className="kr-shloka-source">— {shloka.source}</div>
        </div>
      )}
    </div>
  );
}

const SIGNS_EN = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
function getSignIdx(signName) {
  return Math.max(0, SIGNS_EN.indexOf(signName));
}
